import { getDefaultSortOrder, Table, TableProps, Tag, TextField, Typography, useSelect } from "@pankod/refine-antd";
import { CrudSorting, useNavigation } from "@pankod/refine-core";
import { ShowUserCard } from "components/profile/showUserCard";
import { displayRelativeDate } from "components/utils/displayRelativeDate";
import { LookupButton } from "components/utils/lookupButton";
import { ILookup, ISawAsset, ISawAssetMetadata } from "interfaces";
import { DATAPROVIDER_LOOKUP, RESOURCE_PATH } from "scripts/site";

export declare type ListSawAssetProps = {
  sawAssetData: TableProps<ISawAsset>,
  sorter: CrudSorting,
  metadata: ISawAssetMetadata | undefined
};

const resourcePath = RESOURCE_PATH.SAW_ASSET;

export const ListSawAssetTable: React.FC<ListSawAssetProps> = ({ sawAssetData, sorter, metadata }) => {

  const { show } = useNavigation();

  useSelect<ILookup>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.PEOPLE,
    optionLabel: "name",
    optionValue: "id",
    sort: [
      {
        field: "lastAccessedAt",
        order: "desc"
      },
    ],
  });

  function rowClick(record: ISawAsset) {
    return {
      onClick: () => {
        show(resourcePath, record.id);
      },
    };
  }
  return (
    <Table {...sawAssetData}
      rowKey="id"
      pagination={{
        ...sawAssetData.pagination,
        position: ["bottomRight"],
        showTotal: (total => <Typography.Title level={4} style={{ marginRight: 10 }}>Total {total}</Typography.Title>)
      }}
    >
      <Table.Column
        dataIndex="serialNumber"
        key="serialNumber"
        onCell={rowClick}
        className="mouseHand"
        title="Serial Number"
        render={(value: string) => <TextField value={value} />}
        sorter
      />

      <Table.Column
        dataIndex={["stateManager", "state"]}
        key="stateManager.state"
        className="mouseHand"
        title="State"
        render={(value, record: ISawAsset) => {
          const stateMetadata = metadata?.states?.find(state => state.state === value);
          const fallbackLabel = value ?? "---";
          return (
            <Tag color={stateMetadata?.color ?? "default"}>
              {stateMetadata?.displayName ?? fallbackLabel}
            </Tag>
          );
        }}
        defaultSortOrder={getDefaultSortOrder("stateManager.state", sorter)}
        sorter
        onCell={rowClick}
      />

      <Table.Column
        dataIndex="makeModel"
        key="makeModel"
        onCell={rowClick}
        className="mouseHand"
        title="Make/Model"
        render={(value: string) => <TextField value={value} />}
        sorter
      />

      <Table.Column
        dataIndex="id"
        key="id"
        onCell={rowClick}
        className="mouseHand"
        title="SAW Asset ID"
        render={(_, record: ISawAsset) => <TextField value={record?.sawAssetId ?? record?.id} />}
        sorter
      />

      <Table.Column
        dataIndex="dockSerialNumber"
        key="dockSerialNumber"
        onCell={rowClick}
        className="mouseHand"
        title="Dock Serial Number"
        render={(value: string) => <TextField value={value ?? "---"} />}
        sorter
      />

      <Table.Column
        dataIndex="tcsEmployeeId"
        key="tcsEmployeeId"
        onCell={rowClick}
        className="mouseHand"
        title="Employee Id"
        render={(value?: string) => <TextField value={value || "---"} />}
        sorter
      />
      <Table.Column
        key="stateManager.assignedTo"
        dataIndex={["stateManager", "assignedTo"]}
        title="Emp Name"
        onCell={rowClick}
        className="mouseHand"
        defaultSortOrder={getDefaultSortOrder("stateManager.assignedTo", sorter)}
        sorter
        render={(value: string[]) =>
          value && value.map((r, i) => (
            <div key={i} style={{ marginBottom: 5 }}>
              <ShowUserCard id={r} />
            </div>
          ))
        }
      />

      <Table.Column
        dataIndex="associateLocation"
        key="associateLocation"
        onCell={rowClick}
        className="mouseHand"
        title="Location"
        render={(value: string) => (
          value ? (
            <LookupButton id={value} resource={RESOURCE_PATH.LOCATION} noClick />
          ) : (
            <TextField value="---" />
          )
        )}
        sorter
      />

      <Table.Column
        dataIndex="updatedAt"
        key="updatedAt"
        title="Last Updated"
        render={displayRelativeDate}
        defaultSortOrder={getDefaultSortOrder("updatedAt", sorter)}
        sorter
        onCell={rowClick}
        className="mouseHand"
      />
    </Table>
  );
};